import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, merge, skip } from 'rxjs';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ReleaseNotesComponent } from '../../../modules/dashboard/components/release-notes/release-notes.component';
import { AuthService } from '../../services/auth.service';
import { ReleaseNotesUnreadService } from '../../services/release-notes/release-notes-unread.service';

export const HEADER_NOTIFICATIONS_PANEL_CLASS = 'header-notifications-bell-panel';

const PANEL_WIDTH_PX = 400;
const PANEL_HEIGHT_PX = 720;
const PANEL_OFFSET_PX = 8;
const PANEL_VIEWPORT_MARGIN_PX = 16;

interface PanelLayout {
    left: number;
    top: number;
    width: number;
    height: number;
}

@Component({
    selector: 'app-header-notifications-bell',
    standalone: true,
    imports: [CommonModule, MatIconButton, MatIcon, MatTooltip, TranslocoPipe],
    templateUrl: './header-notifications-bell.component.html',
    styleUrl: './header-notifications-bell.component.scss',
})
export class HeaderNotificationsBellComponent implements OnInit, OnDestroy {
    @ViewChild('bellTrigger', { read: ElementRef })
    private bellTrigger?: ElementRef<HTMLButtonElement>;

    unreadCount = 0;
    isPanelOpen = false;
    private overlayRef?: OverlayRef;
    private panelLayout?: PanelLayout;

    readonly closePanel = (): void => {
        this.overlayRef?.dispose();
        this.overlayRef = undefined;
        this.panelLayout = undefined;
        this.isPanelOpen = false;
    };

    constructor(
        private overlay: Overlay,
        private releaseNotesUnreadService: ReleaseNotesUnreadService,
        private authService: AuthService,
        private transloco: TranslocoService,
        private destroyRef: DestroyRef,
    ) {}

    ngOnInit(): void {
        this.initUnreadCountSubscription();
        this.initUnreadRefreshTriggers();
        this.releaseNotesUnreadService.refresh();
    }

    private initUnreadCountSubscription(): void {
        this.releaseNotesUnreadService.unreadCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((count) => {
            this.unreadCount = count;
        });
    }

    private initUnreadRefreshTriggers(): void {
        merge(this.transloco.langChanges$.pipe(skip(1), distinctUntilChanged()), this.authService.profileHydrated$)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.releaseNotesUnreadService.refresh());
    }

    ngOnDestroy(): void {
        this.closePanel();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.closePanel();
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    onViewportChange(): void {
        if (this.isPanelOpen) {
            this.syncOverlayPosition();
        }
    }

    get badgeLabel(): string {
        return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
    }

    togglePanel(): void {
        if (this.isPanelOpen) {
            this.closePanel();
            return;
        }

        this.openPanel();
    }

    private openPanel(): void {
        const trigger = this.bellTrigger?.nativeElement;
        if (!trigger) {
            return;
        }

        this.panelLayout = this.resolvePanelLayout(trigger);

        this.overlayRef = this.overlay.create({
            positionStrategy: this.createPositionStrategy(this.panelLayout),
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            panelClass: HEADER_NOTIFICATIONS_PANEL_CLASS,
            width: this.panelLayout.width,
            height: this.panelLayout.height,
            maxHeight: this.panelLayout.height,
        });

        this.overlayRef.backdropClick().subscribe(() => this.closePanel());

        const portal = new ComponentPortal(ReleaseNotesComponent);
        const componentRef = this.overlayRef.attach(portal);
        componentRef.setInput('onClose', this.closePanel);

        this.isPanelOpen = true;
    }

    private syncOverlayPosition(): void {
        const trigger = this.bellTrigger?.nativeElement;
        if (!this.overlayRef || !trigger) {
            return;
        }

        this.panelLayout = this.resolvePanelLayout(trigger);
        this.overlayRef.updatePositionStrategy(this.createPositionStrategy(this.panelLayout));
        this.overlayRef.updateSize({
            width: this.panelLayout.width,
            height: this.panelLayout.height,
        });
    }

    private resolvePanelLayout(trigger: HTMLElement): PanelLayout {
        const rect = trigger.getBoundingClientRect();
        const width = Math.min(
            window.innerWidth - PANEL_VIEWPORT_MARGIN_PX * 2,
            window.innerWidth * 0.92,
            PANEL_WIDTH_PX,
        );
        const height = Math.min(
            window.innerHeight - PANEL_VIEWPORT_MARGIN_PX * 2,
            window.innerHeight * 0.85,
            PANEL_HEIGHT_PX,
        );

        const preferredLeft = rect.right - width;
        const maxLeft = window.innerWidth - width - PANEL_VIEWPORT_MARGIN_PX;
        const left = Math.max(PANEL_VIEWPORT_MARGIN_PX, Math.min(preferredLeft, maxLeft));

        const preferredTop = rect.bottom + PANEL_OFFSET_PX;
        const maxTop = window.innerHeight - height - PANEL_VIEWPORT_MARGIN_PX;
        const top = Math.max(PANEL_VIEWPORT_MARGIN_PX, Math.min(preferredTop, maxTop));

        return { left, top, width, height };
    }

    private createPositionStrategy(layout: PanelLayout) {
        return this.overlay.position().global().top(`${layout.top}px`).left(`${layout.left}px`);
    }
}
